export type SecurityType = 'none' | 'starttls' | 'tls';
export type AuthType = 'password' | 'token';
export type SmtpEffectiveSource = 'admin' | 'default';

/** The stored row as the server returns it. It never contains a password or token. */
export interface SmtpSettings {
    id: number | null;
    adminId: number | null;
    provider: 'smtp';
    host: string | null;
    port: number | null;
    securityType: SecurityType;
    authType: AuthType;
    username: string | null;
    fromName: string | null;
    fromEmail: string | null;
    replyTo: string | null;
    isEnabled: boolean;
    isVerified: boolean;
    lastVerifiedAt: string | null;
    lastError: string | null;
    hasPassword: boolean;
    hasAuthToken: boolean;
    /** True when the admin has saved nothing; the other values are then the platform sender. */
    isDefault: boolean;
    /** 'admin' only while the row is both enabled and verified. */
    effectiveSource: SmtpEffectiveSource;
    createdAt: string | null;
    updatedAt: string | null;
}

/** Public view of the platform sender, for display only. */
export interface DefaultSender {
    fromName: string | null;
    fromEmail: string | null;
}

export interface GetSettingsData {
    settings: SmtpSettings;
    default: DefaultSender | null;
}

export interface SettingsData {
    settings: SmtpSettings;
}

export interface VerifyData {
    verified: boolean;
    error: string | null;
    settings: SmtpSettings;
}

/** Partial update: omitted fields keep their stored value. Secrets are write-only. */
export interface SaveSettingsRequest {
    host?: string;
    port?: number;
    securityType?: SecurityType;
    authType?: AuthType;
    username?: string | null;
    password?: string;
    authToken?: string;
    fromEmail?: string;
    fromName?: string | null;
    replyTo?: string | null;
}

/** Server envelope: replies are usually HTTP 200 with the real status in the body. */
export interface SmtpEnvelope<T> {
    status: number;
    message: string;
    data: T;
}

export class SmtpApiError extends Error {
    constructor(public status: number, message: string, public data: any = null) {
        super(message);
        this.name = 'SmtpApiError';
    }

    /** Seconds to wait after a 429 (a minute if the server did not say), or 0. */
    get retryAfterSeconds(): number {
        if (this.status !== 429) {
            return 0;
        }
        const seconds = Number(this.data?.retryAfterSeconds);
        return seconds > 0 ? Math.ceil(seconds) : RATE_LIMIT_FALLBACK_SECONDS;
    }
}

export const RATE_LIMIT_FALLBACK_SECONDS = 60;

export const DEFAULT_PORTS: Record<SecurityType, number> = {
    tls: 465,
    starttls: 587,
    none: 25,
};

// Limits enforced by the server, checked in the browser first.
export const HOST_MAX_LENGTH = 255;
export const USERNAME_MAX_LENGTH = 255;
export const PASSWORD_MAX_LENGTH = 512;
export const AUTH_TOKEN_MAX_LENGTH = 2048;
export const FROM_NAME_MAX_LENGTH = 150;
